import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import { LayoutModule } from '@angular/cdk/layout';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatRadioModule } from '@angular/material/radio';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSliderModule } from '@angular/material/slider';
import { MatSortModule } from '@angular/material/sort';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { environment } from '../environments/environment';
import { HeaderComponent } from './components/header/header.component';
import { RequestsComponent } from './pages/requests/requests.component';
import { ActionsComponent } from './pages/actions/actions.component';
import { LoginComponent } from './pages/login/login.component';
import { RequestsTableComponent } from './components/requests-table/requests-table.component';
import { ApproveFormComponent } from './components/approve-form/approve-form.component';
import { RequestFormComponent } from './components/request-form/request-form.component';
import { NewRequestFormComponent } from './components/new-request-form/new-request-form.component';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { StatusIndicatorComponent } from './components/status-indicator/status-indicator.component';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  HttpClientModule,
} from '@angular/common/http';
import { ConfirmWithdrawComponent } from './components/confirm-withdraw/confirm-withdraw.component';
import { DetailedInfoComponent } from './components/detailed-info/detailed-info.component';
import { ResourceRenewalComponent } from './components/resource-renewal/resource-renewal.component';
import { ActionDetailedInfoComponent } from './components/action-detailed-info/action-detailed-info.component';
import { ActionDetailsComponent } from './components/action-details/action-details.component';
import { RequestHistoryInfoComponent } from './components/request-history-info/request-history-info.component';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { DynamicFormComponent } from './components/dynamic-form/dynamic-form.component';
import { CommonModule } from '@angular/common';
import {
  NgxMatDatetimePickerModule,
  NgxMatTimepickerModule,
  NgxMatNativeDateModule,
} from '@angular-material-components/datetime-picker';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { TagsListComponent } from './components/tags-list/tags-list.component';
import { RejectFormComponent } from './components/reject-form/reject-form.component';
import { AssistanceComponent } from './components/assistance/assistance.component';
import { DelegatesComponent } from './pages/delegates/delegates.component';
import { TransferComponent } from './components/transfer/transfer.component';
import { JwtModule } from '@auth0/angular-jwt';
import { AuthService } from './services/auth.service';
import { AuthInterceptor } from './auth.interceptor';
import { ConfirmDelegateRemovalComponent } from './components/confirm-delegate-removal/confirm-delegate-removal.component';
import { AddDelegatesComponent } from './components/add-delegates/add-delegates.component';
import { ConfirmDelegateCreationComponent } from './components/confirm-delegate-creation/confirm-delegate-creation.component';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { CompleteActionComponent } from './components/complete-action/complete-action.component';
import { ErrorInterceptor } from './interceptors/error.interceptor';
import { ConfirmNewDeprovisioningComponent } from './components/confirm-new-deprovisioning/confirm-new-deprovisioning.component';
import { NgxSpinnerModule } from 'ngx-spinner';
import { ScrollHandlerDirective } from './directives/scroll-handler.directive';
import { ReportComponent } from './components/report/report.component';
import { IncidentsComponent } from './pages/incidents/incidents.component';
import { DragDropFileDirective } from './directives/drag-drop-file.directive';
import { DragDropFileComponent } from './components/drag-drop-file/drag-drop-file.component';
import { ProgressComponent } from './components/progress/progress.component';
import { IncidentInfoComponent } from './components/incident-info/incident-info.component';
import { RequestInfoComponent } from './components/request-info/request-info.component';
import { RequestDetailComponent } from './components/request-detail/request-detail.component';
import { NewRequestErrorSnackbarComponent } from './components/new-request-error-snackbar/new-request-error-snackbar.component';

export function httpTranslateLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}
export function tokenGetter() {
  if (localStorage.getItem('currentUser'))
    return JSON.parse(localStorage.getItem('currentUser')!).credential;
  return '';
}

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    RequestsComponent,
    ActionsComponent,
    LoginComponent,
    RequestsTableComponent,
    ApproveFormComponent,
    RequestFormComponent,
    NewRequestFormComponent,
    StatusIndicatorComponent,
    ConfirmWithdrawComponent,
    DetailedInfoComponent,
    ResourceRenewalComponent,
    ResourceRenewalComponent,
    ActionDetailedInfoComponent,
    ActionDetailsComponent,
    RequestHistoryInfoComponent,
    DynamicFormComponent,
    TagsListComponent,
    RejectFormComponent,
    AssistanceComponent,
    DelegatesComponent,
    TransferComponent,
    ConfirmDelegateRemovalComponent,
    AddDelegatesComponent,
    ConfirmDelegateCreationComponent,
    CompleteActionComponent,
    ConfirmNewDeprovisioningComponent,
    ScrollHandlerDirective,
    ReportComponent,
    IncidentsComponent,
    DragDropFileDirective,
    DragDropFileComponent,
    ProgressComponent,
    IncidentInfoComponent,
    RequestInfoComponent,
    RequestDetailComponent,
    NewRequestErrorSnackbarComponent,
  ],
  imports: [
    BrowserModule,
    CommonModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    MatSnackBarModule,
    NgxSpinnerModule,
    LayoutModule,
    NgSelectModule,
    HttpClientModule,
    ReactiveFormsModule,
    NgbModule,
    FormsModule,
    FontAwesomeModule,
    MatToolbarModule,
    MatButtonModule,
    MatNativeDateModule,
    MatSidenavModule,
    MatBadgeModule,
    MatIconModule,
    MatListModule,
    MatInputModule,
    MatSliderModule,
    MatSelectModule,
    MatRadioModule,
    MatCardModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatTooltipModule,
    MatTabsModule,
    MatButtonToggleModule,
    MatDialogModule,
    MatAutocompleteModule,
    MatCheckboxModule,
    MatDatepickerModule,
    NgxMatDatetimePickerModule,
    NgxMatTimepickerModule,
    NgxMatNativeDateModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production,
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: httpTranslateLoaderFactory,
        deps: [HttpClient],
      },
    }),
    JwtModule.forRoot({
      config: {
        tokenGetter: tokenGetter,
      },
    }),
  ],
  providers: [
    AuthService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
